import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import ApiRequest from '../actions/ApiRequest';

const MaintenanceContext = createContext();

export const useMaintenanceContext = () => {
    const context = useContext(MaintenanceContext);
    if (!context) {
        throw new Error('useMaintenanceContext must be used within a MaintenanceProvider');
    }
    return context;
};

export const MaintenanceProvider = ({ children }) => {
    const [maintenanceState, setMaintenanceState] = useState({
        enabled: false,
        isAdmin: false,
        bypassAllowed: false,
        messages: {
            ar: '',
            en: '',
            he: ''
        },
        loading: true,
        error: null
    });

    const checkMaintenanceStatus = useCallback(async () => {
        try {
            const response = await ApiRequest.get('maintenance_status');

            if (response?.data?.status === 'success') {
                setMaintenanceState({
                    enabled: response.data.maintenance_enabled,
                    isAdmin: response.data.is_admin,
                    bypassAllowed: response.data.bypass_allowed,
                    messages: response.data.messages || { ar: '', en: '', he: '' },
                    loading: false,
                    error: null
                });
            } else {
                setMaintenanceState(prev => ({ ...prev, loading: false }));
            }
        } catch (error) {
            console.error('Error checking maintenance status:', error);
            setMaintenanceState(prev => ({
                ...prev,
                loading: false,
                error: error.message
            }));
        }
    }, []);

    // Check on mount
    useEffect(() => {
        checkMaintenanceStatus();
    }, [checkMaintenanceStatus]);

    // Check periodically (every 2 minutes)
    useEffect(() => {
        const interval = setInterval(checkMaintenanceStatus, 120000);
        return () => clearInterval(interval);
    }, [checkMaintenanceStatus]);

    // Determine if user should be blocked
    const isBlocked = maintenanceState.enabled && !maintenanceState.bypassAllowed;

    // Get message based on current locale
    const getMessage = useCallback((locale = 'en') => {
        const lang = locale?.substring(0, 2) || 'en';
        return maintenanceState.messages[lang] || maintenanceState.messages.en;
    }, [maintenanceState.messages]);

    const value = {
        ...maintenanceState,
        isBlocked,
        getMessage,
        refreshStatus: checkMaintenanceStatus
    };

    return (
        <MaintenanceContext.Provider value={value}>
            {children}
        </MaintenanceContext.Provider>
    );
};

export default MaintenanceContext;
